import { useState, useEffect } from 'react';
import { checkIfLookFeaturesReceived } from "../../../api/look-api";
import { getCurrentLayout } from "../../appLayout";
import {
  isDevMode,
  SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls,
  SQFT_FEATURES
} from "../constants";
import { hardCodeIsHomeDepot, hardCodeIsRenuit } from "../../../misc/fabricatorHardcode";
import { matchesAllowedCombo } from "../helpers/sqft3dArHelper";
import { getNavItems } from "../helpers/sqftHelperUi";

const filterNavItemsForAR3D = (navItems) => {
  return navItems.filter(feature => {
    return feature.part
      && feature.part.dbFeature
      && Array.isArray(feature.part.dbFeature.thirdPartyAR3DUrls)
      && feature.part.dbFeature.thirdPartyAR3DUrls.length > 0;
  });
};

const filterNavItemsByAllowedColorRule = (partId, navItems) => {
  if (
    ![SQFT_FEATURES.top_cabinet, SQFT_FEATURES.bottom_cabinet, SQFT_FEATURES.tall_cabinet].includes(partId)
  ) {
    return navItems;
  }

  if (isDevMode() || (!hardCodeIsHomeDepot() && !hardCodeIsRenuit())) {
    return navItems;
  }

  return navItems.filter(feature => {
    const featureName = feature.part.getFeatureName?.() ?? "";
    const sharedFeatureName = feature.part.getLowerCasedSharedFeatureName?.() ?? "";
    return matchesAllowedCombo(featureName, sharedFeatureName);
  });
};

const filterNavItemsForDisplay = ({
  partId,
  navItems,
  shouldApplyAR3DFilter,
  applyAllowedColorRule,
}) => {
  let filteredNavItems = [...navItems];

  if (shouldApplyAR3DFilter) {
    filteredNavItems = filterNavItemsForAR3D(filteredNavItems);
  }

  if (applyAllowedColorRule) {
    filteredNavItems = filterNavItemsByAllowedColorRule(partId, filteredNavItems);
  }

  return filteredNavItems;
};

const getWasFilteredByAR3D = ({
  shouldApplyAR3DFilter,
  navItemsBeforeFilter,
  navItemsAfterFilter,
}) => {
  return shouldApplyAR3DFilter
    && navItemsBeforeFilter.length > 0
    && navItemsAfterFilter.length === 0;
};

/**
 * Standalone function to fetch features data
 * @param {Array<string>} partIds - Part IDs whose feature groups should be loaded.
 * @param {string} searchText - Text filter applied to navigation items before returning them.
 * @param {Object} selectedFilters - Selected filter values passed to the feature navigation builder.
 * @param {boolean} shouldGetModelsOnlyWithThirdPartyAR3DUrls - When true, keep only items that have `thirdPartyAR3DUrls`
 * for part IDs listed in `SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls`.
 * @param {boolean} applyAllowedColorRule - When true, apply the Home Depot / Renuit allowed cabinet color restriction.
 * @param {boolean} useAllWizards - When true, wizard-backed nav items are merged from all compatible wizard flows
 * instead of only the active wizard.
 * @param {boolean} includeHidePriceNoCart - When true, include wizard groups marked with `hidePriceNoCart`.
 * This is useful for read-only pickers and previews where we still want to show features even if they should not be
 * added to cart from the wizard step itself.
 * @returns {Promise<Object>} Features data object
 */
export const fetchFeatures = async (
  partIds,
  searchText = "",
  selectedFilters= {},
  shouldGetModelsOnlyWithThirdPartyAR3DUrls = false,
  applyAllowedColorRule = true,
  useAllWizards = false,
  includeHidePriceNoCart = false
) => {
  const layout = getCurrentLayout();
  const newFeaturesData = {};

  for (const partId of partIds) {
    const featureGroup = layout.getFeatureGroupByPartId(partId);
    let navItemsFromFeatureGroup = [];
    let navItemsFromFeatureGroupFiltered = [];
    let navItemsFromTheWizard = [];
    let navItemsFromTheWizardFiltered = [];
    const shouldApplyAR3DFilter = shouldGetModelsOnlyWithThirdPartyAR3DUrls &&
      SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls.includes(partId);

    if (featureGroup) {
      // If features are not received and no request is in progress, wait for the promise
      if (!featureGroup.allFeaturesReceived && !featureGroup.requestInProgress) {
        await checkIfLookFeaturesReceived(featureGroup);
      }

      navItemsFromFeatureGroup = layout.designNavigationItems(
        { part: featureGroup },
        selectedFilters,
        searchText,
        null,
        "all",
        [],
        { firstFeature: null, firstConditions: null },
      );

      navItemsFromTheWizard = getNavItems(
        { part: featureGroup },
        searchText,
        selectedFilters,
        null,
        "all",
        useAllWizards,
        includeHidePriceNoCart
      );

      navItemsFromFeatureGroupFiltered = filterNavItemsForDisplay({
        partId,
        navItems: navItemsFromFeatureGroup,
        shouldApplyAR3DFilter,
        applyAllowedColorRule,
      });

      navItemsFromTheWizardFiltered = filterNavItemsForDisplay({
        partId,
        navItems: navItemsFromTheWizard,
        shouldApplyAR3DFilter,
        applyAllowedColorRule,
      });
    }

    newFeaturesData[partId] = {
      navItemsFromFeatureGroup,
      navItemsFromFeatureGroupFiltered,
      navItemsFromTheWizard,
      navItemsFromTheWizardFiltered,
      wasFilteredByAR3DFromFeatureGroup: getWasFilteredByAR3D({
        shouldApplyAR3DFilter,
        navItemsBeforeFilter: navItemsFromFeatureGroup,
        navItemsAfterFilter: navItemsFromFeatureGroupFiltered,
      }),
      wasFilteredByAR3DFromTheWizard: getWasFilteredByAR3D({
        shouldApplyAR3DFilter,
        navItemsBeforeFilter: navItemsFromTheWizard,
        navItemsAfterFilter: navItemsFromTheWizardFiltered,
      }),
      featureGroup: featureGroup,
    };
  }

  return newFeaturesData;
};

/**
 * React hook to fetch and manage features data with loading state
 * @param {Array<string>} partIds - Part IDs whose feature groups should be loaded.
 * @param {string} searchText - Text filter applied to navigation items before returning them.
 * @param {Object} selectedFilters - Selected filter values passed to the feature navigation builder.
 * @param {boolean} shouldGetModelsOnlyWithThirdPartyAR3DUrls - When true, keep only items that have `thirdPartyAR3DUrls`
 * for part IDs listed in `SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls`.
 * @param {boolean} applyAllowedColorRule - When true, apply the Home Depot / Renuit allowed cabinet color restriction.
 * @param {boolean} useAllWizards - When true, wizard-backed nav items are merged from all compatible wizard flows
 * instead of only the active wizard.
 * @param {boolean} includeHidePriceNoCart - When true, include wizard groups marked with `hidePriceNoCart`.
 * @returns {Object} Object containing featuresData and isLoading state
 */
const useFetchFeatures = (
  partIds,
  searchText = "",
  selectedFilters,
  shouldGetModelsOnlyWithThirdPartyAR3DUrls = false,
  applyAllowedColorRule = true,
  useAllWizards = false,
  includeHidePriceNoCart = false
) => {
  const [featuresData, setFeaturesData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Serialize to avoid re-triggering effect on new object/array references
  const partIdsKey = JSON.stringify(partIds);
  const selectedFiltersKey = JSON.stringify(selectedFilters ?? {});
  const useAllWizardsKey = useAllWizards;

  useEffect(() => {
    let isMounted = true;

    const loadFeatures = async () => {
      setIsLoading(true);

      try {
        const data = await fetchFeatures(
          JSON.parse(partIdsKey),
          searchText,
          JSON.parse(selectedFiltersKey),
          shouldGetModelsOnlyWithThirdPartyAR3DUrls,
          applyAllowedColorRule,
          useAllWizardsKey,
          includeHidePriceNoCart
        );

        if (isMounted) {
          setFeaturesData(data);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching features:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFeatures();

    return () => {
      isMounted = false;
    };
  }, [partIdsKey, searchText, selectedFiltersKey, shouldGetModelsOnlyWithThirdPartyAR3DUrls, applyAllowedColorRule, useAllWizardsKey, includeHidePriceNoCart]);

  return { featuresData, isLoading };
};

export default useFetchFeatures;
